import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';

import type { BrandSummary } from '../../models/brand.models';
import { BrandApiService } from '../../services/brand-api.service';
import { normalizeApiError } from '../../utils/error-normalizer';

interface BrandAction {
  id: 'edit' | 'default' | 'brand-id' | 'delete';
  label: string;
  icon: string;
  destructive?: boolean;
}

const BRAND_ID_COPIED_MESSAGE = 'Brand ID copied.';
const BRAND_ID_COPY_FAILED_MESSAGE = 'Unable to copy Brand ID. Please copy it manually.';
const COPY_TOAST_TIMEOUT_MS = 3000;

@Component({
  selector: 'app-branding',
  imports: [CommonModule, FormsModule],
  templateUrl: './branding.component.html',
  styleUrl: './branding.component.scss',
})
export class BrandingComponent implements OnInit {
  readonly brandActions: BrandAction[] = [
    { id: 'edit', label: 'Edit Brand', icon: '✎' },
    { id: 'default', label: 'Set as default', icon: '★' },
    { id: 'brand-id', label: 'Use BrandID', icon: '⧉' },
    { id: 'delete', label: 'Delete', icon: '⌫', destructive: true },
  ];

  brands: BrandSummary[] = [];
  loading = false;
  errorMessage = '';
  copyFeedbackMessage = '';
  copyErrorMessage = '';
  openMenuBrandId: string | null = null;
  settingDefaultBrandId: string | null = null;
  copiedBrandId: string | null = null;

  searchQuery = '';
  sortBy: 'recent' | 'name' | 'default' = 'recent';
  viewMode: 'list' | 'grid' = 'list';

  get filteredBrands(): BrandSummary[] {
    let list = [...this.brands];
    const query = this.searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter(
        (brand) =>
          brand.name.toLowerCase().includes(query) ||
          (brand.emailDisplayName && brand.emailDisplayName.toLowerCase().includes(query)) ||
          brand.brandId.toLowerCase().includes(query),
      );
    }

    if (this.sortBy === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (this.sortBy === 'default') {
      list.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    } else {
      list.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    }

    return list;
  }

  private readonly brandsApi = inject(BrandApiService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private copyFeedbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadBrands();
  }

  createBrand(): void {
    void this.router.navigate(['/settings/branding/new']);
  }

  handleBrandAction(actionId: BrandAction['id'], brand: BrandSummary): void {
    this.closeMenus();

    if (actionId === 'edit') {
      void this.router.navigate(['/settings/branding', brand.brandId, 'edit']);
      return;
    }

    if (actionId === 'default' && !brand.isDefault) {
      this.setDefaultBrand(brand);
      return;
    }

    if (actionId === 'brand-id') {
      void this.copyBrandId(brand);
    }
  }

  toggleMenu(brandId: string): void {
    this.openMenuBrandId = this.openMenuBrandId === brandId ? null : brandId;
  }

  isMenuOpen(brandId: string): boolean {
    return this.openMenuBrandId === brandId;
  }

  brandInitials(brand: BrandSummary): string {
    return brand.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  trackByBrandId(_: number, brand: BrandSummary): string {
    return brand.id;
  }

  visibleBrandActions(brand: BrandSummary): BrandAction[] {
    return this.brandActions.filter((action) => action.id !== 'default' || !brand.isDefault);
  }

  isSettingDefault(brandId: string): boolean {
    return this.settingDefaultBrandId === brandId;
  }

  copyActionAriaLabel(brand: BrandSummary): string {
    return this.copiedBrandId === brand.brandId ? 'Brand ID copied' : `Copy Brand ID for ${brand.name}`;
  }

  dismissCopyFeedback(): void {
    this.clearCopyFeedback();
    this.copiedBrandId = null;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('[data-brand-menu]')) {
      return;
    }

    this.closeMenus();
  }

  private loadBrands(): void {
    this.loading = true;
    this.errorMessage = '';

    this.brandsApi
      .listBrands()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.brands = response.items;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load brands. Please try again.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private closeMenus(): void {
    this.openMenuBrandId = null;
  }

  private async copyBrandId(brand: BrandSummary): Promise<void> {
    this.clearCopyFeedback();
    this.copiedBrandId = null;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }

      await navigator.clipboard.writeText(brand.brandId);
      this.copyFeedbackMessage = BRAND_ID_COPIED_MESSAGE;
      this.copiedBrandId = brand.brandId;
      this.scheduleCopyFeedbackDismiss();
    } catch {
      this.copyErrorMessage = BRAND_ID_COPY_FAILED_MESSAGE;
    } finally {
      this.cdr.markForCheck();
    }
  }

  private setDefaultBrand(brand: BrandSummary): void {
    this.settingDefaultBrandId = brand.id;
    this.errorMessage = '';

    this.brandsApi
      .updateBrand(brand.brandId, { isDefault: true })
      .pipe(
        finalize(() => {
          this.settingDefaultBrandId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.loadBrands();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to update the default brand. Please try again.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private clearCopyFeedback(): void {
    this.copyFeedbackMessage = '';
    this.copyErrorMessage = '';

    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId);
      this.copyFeedbackTimeoutId = null;
    }
  }

  private scheduleCopyFeedbackDismiss(): void {
    if (this.copyFeedbackTimeoutId) {
      clearTimeout(this.copyFeedbackTimeoutId);
    }

    this.copyFeedbackTimeoutId = setTimeout(() => {
      this.copyFeedbackMessage = '';
      this.copiedBrandId = null;
      this.copyFeedbackTimeoutId = null;
      this.cdr.markForCheck();
    }, COPY_TOAST_TIMEOUT_MS);
  }
}
